import { TimePeriod } from '@flakiness/server/common/timePeriod.js';
import { SlDropdown } from '@shoelace-style/shoelace';
import { css, html, LitElement, nothing, PropertyValues } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { Temporal } from 'temporal-polyfill';
import { rangeLabel } from './period-chart.js';

/** The granularities the picker picks in. */
export type PickerPeriod = 'day' | 'usweek' | 'month' | 'quarter';

export class FKPeriodSelectEvent extends Event {
  static readonly eventName = 'fk-select';
  /** The last day of the picked period, or undefined for the current period. */
  readonly day: Temporal.PlainDate|undefined;

  constructor(day: Temporal.PlainDate|undefined) {
    super(FKPeriodSelectEvent.eventName, {
      bubbles: true,
      composed: true,
      cancelable: false,
    });
    this.day = day;
  }
}

const CURRENT_LABELS: Record<PickerPeriod, string> = {
  day: 'Today',
  usweek: 'This week',
  month: 'This month',
  quarter: 'This quarter',
};

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function periodStart(period: PickerPeriod, day: Temporal.PlainDate): Temporal.PlainDate {
  return TimePeriod.periodStart(period, day.toPlainDateTime()).toPlainDate();
}

function periodLastDay(period: PickerPeriod, day: Temporal.PlainDate): Temporal.PlainDate {
  return TimePeriod.lastDay(TimePeriod.periodEnd(period, day.toPlainDateTime()));
}

const isAfter = (a: Temporal.PlainDate, b: Temporal.PlainDate) => Temporal.PlainDate.compare(a, b) > 0;
const shortMonth = (day: Temporal.PlainDate) => day.toLocaleString('en-US', { month: 'short' });

/** How a past period reads: "Oct 10, 2026", "May 10 – 16, 2026", "May 2026", "Q2 2026". */
function periodLabel(period: PickerPeriod, day: Temporal.PlainDate): string {
  if (period === 'day')
    return day.toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  if (period === 'usweek')
    return rangeLabel(periodStart(period, day), periodLastDay(period, day));
  if (period === 'month')
    return day.toLocaleString('en-US', { month: 'short', year: 'numeric' });
  return `Q${Math.ceil(day.month / 3)} ${day.year}`;
}

/**
 * A button naming a period, "Today" or "This month" unless an earlier one is picked,
 * and a picker for it in the given granularity: a day or a week from a month's
 * calendar, a month or a quarter from a year's. Periods that start after today
 * cannot be picked.
 */
@customElement('period-picker')
export class PeriodPicker extends LitElement {
  @property({ attribute: false }) period: PickerPeriod = 'day';
  /** A day in the picked period. Undefined is the current period. */
  @property({ attribute: false }) day?: Temporal.PlainDate;
  /** The time zone that decides which day is today. */
  @property({ attribute: false }) timezone?: string;

  // The first day of the month (for days and weeks) or of the year (for months and
  // quarters) the picker shows.
  @state() private _view?: Temporal.PlainDate;

  @query('sl-dropdown') private _dropdown?: SlDropdown;

  protected override willUpdate(changedProperties: PropertyValues): void {
    if (!this._view || changedProperties.has('period') || changedProperties.has('day'))
      this._view = this._viewOf(this._picked());
  }

  render() {
    const today = this._today();
    const picked = this._picked();
    const isCurrent = !isAfter(today, periodLastDay(this.period, picked));
    return html`
      <sl-dropdown placement=bottom-start distance=10 @sl-show=${(event: Event) => {
        // Reopening starts from the picked period again, wherever the last browse ended.
        if (event.target === this._dropdown)
          this._view = this._viewOf(picked);
      }}>
        <sl-button slot=trigger caret>
          <sl-icon slot=prefix name=calendar4-range></sl-icon>
          ${isCurrent ? CURRENT_LABELS[this.period] : periodLabel(this.period, picked)}
        </sl-button>
        <div class=panel>
          ${this._view ? this._renderPanel(this._view, picked, today) : nothing}
          <div class=footer>
            <sl-button size=small ?disabled=${isCurrent} @click=${() => this._pick(today)}>${CURRENT_LABELS[this.period]}</sl-button>
          </div>
        </div>
      </sl-dropdown>
    `;
  }

  private _renderPanel(view: Temporal.PlainDate, picked: Temporal.PlainDate, today: Temporal.PlainDate) {
    const byMonth = this.period === 'day' || this.period === 'usweek';
    const step = byMonth ? { months: 1 } : { years: 1 };
    const next = view.add(step);
    return html`
      <div class=header>
        <sl-icon-button name=chevron-left label=${byMonth ? 'Previous month' : 'Previous year'}
          @click=${() => this._view = view.subtract(step)}
        ></sl-icon-button>
        <span class=title>${byMonth ? view.toLocaleString('en-US', { month: 'long', year: 'numeric' }) : view.year}</span>
        <sl-icon-button name=chevron-right label=${byMonth ? 'Next month' : 'Next year'}
          ?disabled=${isAfter(next, today)}
          @click=${() => this._view = next}
        ></sl-icon-button>
      </div>
      ${byMonth ? this._renderMonth(view, picked, today) : this._renderYear(view, picked, today)}
    `;
  }

  /** A month's calendar. Picking weeks, each row is one button; picking days, each day is. */
  private _renderMonth(view: Temporal.PlainDate, picked: Temporal.PlainDate, today: Temporal.PlainDate) {
    const monthEnd = view.add({ months: 1 }).subtract({ days: 1 });
    const weekStarts: Temporal.PlainDate[] = [];
    for (let start = periodStart('usweek', view); !isAfter(start, monthEnd); start = start.add({ days: 7 }))
      weekStarts.push(start);
    const pickedStart = periodStart(this.period, picked);
    const dayText = (day: Temporal.PlainDate) => html`
      <span class=${classMap({ 'day-text': true, outside: day.month !== view.month, today: day.equals(today) })}>${day.day}</span>
    `;

    return html`
      <div class=weekdays>${WEEKDAYS.map(weekday => html`<span>${weekday}</span>`)}</div>
      ${weekStarts.map(weekStart => {
        const days = [0, 1, 2, 3, 4, 5, 6].map(offset => weekStart.add({ days: offset }));
        if (this.period === 'usweek') {
          return html`
            <button type=button class=${classMap({ week: true, selected: weekStart.equals(pickedStart) })}
              ?disabled=${isAfter(weekStart, today)}
              @click=${() => this._pick(weekStart)}
            >${days.map(dayText)}</button>
          `;
        }
        return html`
          <div class=week>
            ${days.map(day => html`
              <button type=button class=${classMap({ day: true, selected: day.equals(pickedStart) })}
                ?disabled=${isAfter(day, today)}
                @click=${() => this._pick(day)}
              >${dayText(day)}</button>
            `)}
          </div>
        `;
      })}
    `;
  }

  /** A year's months or quarters. */
  private _renderYear(view: Temporal.PlainDate, picked: Temporal.PlainDate, today: Temporal.PlainDate) {
    const isQuarters = this.period === 'quarter';
    const starts = isQuarters
      ? [1, 4, 7, 10].map(month => view.with({ month }))
      : [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map(month => view.with({ month }));
    const pickedStart = periodStart(this.period, picked);
    const todayStart = periodStart(this.period, today);
    return html`
      <div class=${isQuarters ? 'quarters' : 'months'}>
        ${starts.map(start => html`
          <button type=button class=${classMap({ cell: true, selected: start.equals(pickedStart), today: start.equals(todayStart) })}
            ?disabled=${isAfter(start, today)}
            @click=${() => this._pick(start)}
          >
            ${isQuarters ? html`
              <span>Q${Math.ceil(start.month / 3)}</span>
              <span class=months-hint>${shortMonth(start)} – ${shortMonth(start.add({ months: 2 }))}</span>
            ` : shortMonth(start)}
          </button>
        `)}
      </div>
    `;
  }

  private _pick(day: Temporal.PlainDate) {
    this._dropdown?.hide();
    const lastDay = periodLastDay(this.period, day);
    this.dispatchEvent(new FKPeriodSelectEvent(isAfter(this._today(), lastDay) ? lastDay : undefined));
  }

  private _today() {
    return Temporal.Now.plainDateISO(this.timezone);
  }

  private _picked() {
    return this.day ?? this._today();
  }

  private _viewOf(day: Temporal.PlainDate) {
    return this.period === 'day' || this.period === 'usweek' ? day.with({ day: 1 }) : day.with({ month: 1, day: 1 });
  }

  static styles = css`
    .panel {
      display: flex;
      flex-direction: column;
      gap: var(--sl-spacing-2x-small);
      background-color: white;
      padding: var(--sl-spacing-small);
      border: 1px solid var(--fk-color-border);
      box-shadow: var(--fk-shadow-floating-small);
      border-radius: var(--sl-border-radius-large);
    }

    .header {
      display: flex;
      align-items: center;
      justify-content: space-between;
    }

    .title {
      font-weight: var(--sl-font-weight-semibold);
    }

    .footer {
      display: flex;
      justify-content: flex-end;
      margin-top: var(--sl-spacing-x-small);
    }

    .weekdays, .week {
      display: grid;
      grid-template-columns: repeat(7, 2.25rem);
    }

    .weekdays span {
      text-align: center;
      font-size: var(--sl-font-size-x-small);
      font-weight: var(--sl-font-weight-semibold);
      color: var(--sl-color-neutral-500);
      padding: var(--sl-spacing-2x-small) 0;
    }

    .months, .quarters {
      display: grid;
      grid-template-columns: repeat(4, 4rem);
      gap: var(--sl-spacing-2x-small);
    }

    button {
      font: inherit;
      color: inherit;
      background: none;
      border: none;
      padding: 0;
      border-radius: var(--sl-border-radius-medium);
      cursor: pointer;
    }

    button:hover {
      background: var(--sl-color-neutral-100);
    }

    .day, .day-text, .cell {
      display: flex;
      align-items: center;
      justify-content: center;
      height: 2.25rem;
    }

    .day-text {
      width: 100%;
      border-radius: var(--sl-border-radius-medium);
    }

    .cell {
      flex-direction: column;
    }

    .quarters .cell {
      height: 3rem;
    }

    .months-hint {
      font-size: var(--sl-font-size-x-small);
      color: var(--sl-color-neutral-500);
    }

    .outside {
      color: var(--sl-color-neutral-400);
    }

    /* Today, or the month or quarter that holds it. */
    .today {
      box-shadow: inset 0 0 0 1px var(--sl-color-neutral-400);
    }

    button:disabled, button:disabled * {
      color: var(--sl-color-neutral-300);
      background: none;
      cursor: not-allowed;
    }

    button.selected, button.selected * {
      background: var(--sl-color-primary-600);
      color: white;
    }

    .selected .outside {
      color: var(--sl-color-primary-100);
    }

    .selected .today, .today.selected {
      box-shadow: none;
    }
  `;
}
